/**
 * Insights Page - Stripe-style analytics dashboard
 *
 * Features:
 * - KPI cards with trends
 * - Insight cards by type
 * - Regenerate insights
 */

import { useWorkspace } from '../context/WorkspaceContext';
import {
  getInsightsByWorkspace,
  generateWorkspaceInsights,
  getDashboardData,
} from '../lib/services/insights.service';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  SparklesIcon,
  BanknotesIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { Insight, InsightType } from '../lib/types';

const INSIGHT_TYPE_CONFIG: Record<InsightType, { label: string; color: string; icon: typeof LightBulbIcon }> = {
  summary: { label: 'Summary', color: 'blue', icon: ChartBarIcon },
  recommendation: { label: 'Recommendation', color: 'purple', icon: LightBulbIcon },
  alert: { label: 'Alert', color: 'red', icon: ExclamationTriangleIcon },
  trend: { label: 'Trend', color: 'green', icon: ArrowTrendingUpIcon },
  kpi: { label: 'KPI', color: 'indigo', icon: SparklesIcon },
};

export function InsightsPage() {
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();

  if (!currentWorkspace) return null;

  const insights = getInsightsByWorkspace(currentWorkspace.id);
  const dashboardData = getDashboardData(currentWorkspace.id);

  const handleRegenerateInsights = () => {
    generateWorkspaceInsights(currentWorkspace.id);
    refreshWorkspaces();
  };

  // Group insights by type
  const alertInsights = insights.filter((i) => i.type === 'alert');
  const recommendationInsights = insights.filter((i) => i.type === 'recommendation');
  const summaryInsights = insights.filter((i) => i.type === 'summary' || i.type === 'kpi');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Insights & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            AI-powered analysis of your business performance
          </p>
        </div>
        <button onClick={handleRegenerateInsights} className="btn-secondary btn-sm">
          <ArrowPathIcon className="h-4 w-4" />
          Regenerate
        </button>
      </div>

      {/* KPI Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChartBarIcon className="h-5 w-5 text-indigo-500" />
          Key Performance Indicators
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <EnhancedKPICard 
            label="Revenue" 
            value={`₩${dashboardData.crmSummary.totalDealValue.toLocaleString()}`} 
            trend={dashboardData.kpis.find(k => k.id === 'kpi-pipeline-value')?.trend || 'stable'} 
            change={dashboardData.kpis.find(k => k.id === 'kpi-pipeline-value')?.change || 0} 
            icon={BanknotesIcon} 
            color="emerald"
          />
          <EnhancedKPICard 
            label="Active Leads" 
            value={dashboardData.crmSummary.total.toString()} 
            trend={dashboardData.kpis.find(k => k.id === 'kpi-new-leads')?.trend || 'stable'} 
            change={dashboardData.kpis.find(k => k.id === 'kpi-new-leads')?.change || 0} 
            icon={UsersIcon} 
            color="blue"
          />
          <EnhancedKPICard 
            label="Completion Rate" 
            value={`${dashboardData.kpis.find(k => k.id === 'kpi-completion-rate')?.value || 0}%`} 
            trend={dashboardData.kpis.find(k => k.id === 'kpi-completion-rate')?.trend || 'stable'} 
            change={dashboardData.kpis.find(k => k.id === 'kpi-completion-rate')?.change || 0} 
            icon={ClipboardDocumentCheckIcon} 
            color="indigo"
          />
          <EnhancedKPICard 
            label="Conversion Rate" 
            value={`${dashboardData.kpis.find(k => k.id === 'kpi-conversion-rate')?.value || 0}%`} 
            trend={dashboardData.kpis.find(k => k.id === 'kpi-conversion-rate')?.trend || 'stable'} 
            change={dashboardData.kpis.find(k => k.id === 'kpi-conversion-rate')?.change || 0} 
            icon={SparklesIcon} 
            color="amber"
          />
        </div>
      </div>

      {/* Alerts */}
      {alertInsights.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            Alerts
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {alertInsights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendationInsights.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <LightBulbIcon className="h-5 w-5 text-purple-500" />
            Recommendations
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {recommendationInsights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        </div>
      )}

      {/* Summaries */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChartBarIcon className="h-5 w-5 text-blue-500" />
          Summary & Metrics
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {summaryInsights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </div>
      </div>

      {/* Empty state */}
      {insights.length === 0 && (
        <div className="text-center py-12">
          <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No insights yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add more data to generate AI-powered insights
          </p>
          <button onClick={handleRegenerateInsights} className="btn-primary">
            Generate Insights
          </button>
        </div>
      )}

      {/* Quick Stats Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Tasks</td>
                <td className="font-semibold">{dashboardData.taskSummary.total}</td>
                <td><span className="badge badge-info">Active</span></td>
              </tr>
              <tr>
                <td>Tasks in Triage</td>
                <td className="font-semibold">{dashboardData.taskSummary.byStatus.triage}</td>
                <td>
                  {dashboardData.taskSummary.byStatus.triage > 3 ? (
                    <span className="badge badge-warning">Needs Attention</span>
                  ) : (
                    <span className="badge badge-success">Good</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Overdue Tasks</td>
                <td className="font-semibold">{dashboardData.taskSummary.overdue}</td>
                <td>
                  {dashboardData.taskSummary.overdue > 0 ? (
                    <span className="badge badge-danger">Urgent</span>
                  ) : (
                    <span className="badge badge-success">Clear</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Total Contacts</td>
                <td className="font-semibold">{dashboardData.crmSummary.total}</td>
                <td><span className="badge badge-info">Tracked</span></td>
              </tr>
              <tr>
                <td>Pipeline Value</td>
                <td className="font-semibold">₩{dashboardData.crmSummary.totalDealValue.toLocaleString()}</td>
                <td><span className="badge badge-success">Growing</span></td>
              </tr>
              <tr>
                <td>New Leads (This Week)</td>
                <td className="font-semibold">{dashboardData.crmSummary.newLeadsThisWeek}</td>
                <td>
                  {dashboardData.crmSummary.newLeadsThisWeek > 2 ? (
                    <span className="badge badge-success">Strong</span>
                  ) : (
                    <span className="badge badge-gray">Stable</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Active Team Members</td>
                <td className="font-semibold">{dashboardData.teamSummary.active}</td>
                <td><span className="badge badge-info">Active</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EnhancedKPICard({ 
  label, 
  value, 
  trend, 
  change, 
  icon: Icon, 
  color 
}: { 
  label: string; 
  value: string; 
  trend: 'up' | 'down' | 'stable'; 
  change: number; 
  icon: any; 
  color: string;
}) {
  const TrendIcon = trend === 'up' ? ArrowTrendingUpIcon : trend === 'down' ? ArrowTrendingDownIcon : MinusIcon;
  
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="card-compact border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={clsx('p-2 rounded-lg', colorMap[color] || 'bg-gray-50 text-gray-600')}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="flex items-center gap-1 mt-4">
        <div className={clsx(
          'flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold',
          trend === 'up' ? 'bg-green-50 text-green-700' : 
          trend === 'down' ? 'bg-red-50 text-red-700' : 
          'bg-gray-50 text-gray-700'
        )}>
          <TrendIcon className="h-3 w-3" />
          {change !== 0 && <span>{change}%</span>}
        </div>
        <span className="text-xs text-gray-400">from last month</span>
      </div>
    </div>
  );
}

function InsightCard({ insight }: { insight: Insight }) {
  const config = INSIGHT_TYPE_CONFIG[insight.type];
  const Icon = config.icon;

  const priorityColors: Record<string, string> = {
    info: 'border-l-blue-400',
    action: 'border-l-amber-400',
    urgent: 'border-l-red-500',
  };

  return (
    <div className={clsx('card border-l-4', priorityColors[insight.priority] || 'border-l-gray-300')}>
      <div className="flex items-start gap-3">
        <div className={clsx(
          'p-2 rounded-lg',
          config.color === 'blue' ? 'bg-blue-100 text-blue-600' :
          config.color === 'purple' ? 'bg-purple-100 text-purple-600' :
          config.color === 'red' ? 'bg-red-100 text-red-600' :
          config.color === 'green' ? 'bg-green-100 text-green-600' :
          'bg-indigo-100 text-indigo-600'
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={clsx(
              'badge',
              config.color === 'blue' ? 'badge-info' :
              config.color === 'purple' ? 'badge-purple' :
              config.color === 'red' ? 'badge-danger' :
              config.color === 'green' ? 'badge-success' :
              'badge-primary'
            )}>
              {config.label}
            </span>
            {insight.priority === 'urgent' && (
              <span className="badge badge-danger">Urgent</span>
            )}
            {insight.priority === 'action' && (
              <span className="badge badge-warning">Action Required</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900">{insight.title}</h3>
          <p className="text-sm text-gray-600 mt-1">{insight.summary}</p>

          {/* Show trend if available */}
          {insight.trend && insight.change !== undefined && (
            <div className={clsx(
              'flex items-center gap-1 mt-2 text-xs font-medium',
              insight.trend === 'up' ? 'text-green-600' : insight.trend === 'down' ? 'text-red-600' : 'text-gray-500'
            )}>
              {insight.trend === 'up' ? (
                <ArrowTrendingUpIcon className="h-3 w-3" />
              ) : insight.trend === 'down' ? (
                <ArrowTrendingDownIcon className="h-3 w-3" />
              ) : (
                <MinusIcon className="h-3 w-3" />
              )}
              <span>{insight.change > 0 ? '+' : ''}{insight.change}% change</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsightsPage;
